<template>
  <transition
    :name="`fade-${duration}`"
    :mode="mode"
    :appear="appear"
    v-bind="$attrs"
    :class="[{ 'fade-expand': expand }]"
    @enter="enter"
    @after-enter="afterEnter"
    @leave="leave"
  >
    <slot />
  </transition>
</template>

<script>
export default {
  name: "TransitionFade",
  props: {
    mode: {
      type: String,
      default: "out-in",
    },
    duration: {
      type: Number,
      default: 200,
      validator: (duration) => [200, 300, 500].includes(duration),
    },
    expand: {
      type: Boolean,
      default: false,
    },
    appear: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    // https://markus.oberlehner.net/blog/transition-to-height-auto-with-vue/
    enter(element) {
      if (!this.expand) {
        return;
      }

      /* eslint-disable no-param-reassign */

      const { width } = getComputedStyle(element);

      element.style.width = width;
      element.style.position = "absolute";
      element.style.visibility = "hidden";
      element.style.height = "auto";

      const { height } = getComputedStyle(element);

      element.style.width = null;
      element.style.position = null;
      element.style.visibility = null;
      element.style.height = 0;

      // Force repaint to make sure the
      // animation is triggered correctly.
      // eslint-disable-next-line no-unused-expressions
      getComputedStyle(element).height;

      // Trigger the animation.
      // We use `requestAnimationFrame` because we need
      // to make sure the browser has finished
      // painting after setting the `height`
      // to `0` in the line above.
      requestAnimationFrame(() => {
        element.style.height = height;
      });
    },

    afterEnter(element) {
      if (!this.expand) {
        return;
      }

      element.style.height = "auto";
    },

    leave(element) {
      if (!this.expand) {
        return;
      }

      const { height } = getComputedStyle(element);

      element.style.height = height;

      // Force repaint to make sure the
      // animation is triggered correctly.
      // eslint-disable-next-line no-unused-expressions
      getComputedStyle(element).height;

      requestAnimationFrame(() => {
        element.style.height = 0;
      });
    },
  },
};
</script>

<style>
.fade-expand {
  will-change: height;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000px;
}

.fade-200-enter-active,
.fade-200-leave-active {
  opacity: 1;
  transition:
    opacity 200ms ease,
    height 200ms ease;
}

.fade-200-enter-from,
.fade-200-leave-to {
  opacity: 0;

  &.fade-expand {
    height: 0;
  }
}

.fade-300-enter-active,
.fade-300-leave-active {
  opacity: 1;
  transition:
    opacity 300ms ease,
    height 300ms ease;
}

.fade-300-enter-from,
.fade-300-leave-to {
  opacity: 0;

  &.fade-expand {
    height: 0;
  }
}

.fade-500-enter-active,
.fade-500-leave-active {
  opacity: 1;
  transition:
    opacity 500ms ease,
    height 500ms ease;
}

.fade-500-enter-from,
.fade-500-leave-to {
  opacity: 0;

  &.fade-expand {
    height: 0;
  }
}
</style>
